import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { items, table, tablesOf } from '../../lib/manifest';
import { useHub } from '../context';
import { countUnder, RecordRow } from '../records';
import { inner, type TriumphNode } from '../triumphs';
import { Bar, Check, FillRow, Heading, Labelled, ListRow, Message, Pic, SmallButton, Split } from '../ui';
import { PrivateGate, Vendors } from './CharacterTab';

/** Port of the app's Collections tab. */
export default function CollectionsTab({ sub }: { sub: string }) {
  const h = useHub();
  if (!h.defs) return <Message title="Collections" text="Reading collections from Bungie's database (once per Destiny update)..." />;
  switch (sub) {
    case 'Lore': return <Lore />;
    case 'Medals': return <Tree id="Medals" title="Medals" blurb="The medals you have earned in Crucible, Gambit and the rest, with how many times you earned each." root={h.defs.more.medals} fill={(n) => <MedalCards node={n} />} count={() => ''} />;
    case 'Stat Trackers': return <Tree id="Trackers" title="Stat Trackers" blurb="The numbers the game keeps for your account: the ones you can show on an emblem." root={h.defs.more.metrics} fill={(n) => <MetricTiles node={n} />} count={() => ''} />;
    case 'Patterns & Catalysts': return <Patterns />;
  }
  if (!h.hub!.hasCollections) return <Message title="Collections are hidden" text="This player's Bungie privacy settings hide their collections." />;
  if (sub === 'Sets') return <Sets />;
  if (sub === 'Armor Set Bonuses') return <ArmorSetBonuses />;
  if (sub === 'Tenets') return <PrivateGate title="Tenets" what="The Tenets are in-game shops (Bravery, Sacrifice, Devotion, Death): what each sells and what you already have."><Vendors tenets /></PrivateGate>;
  return <Items explorer={sub === 'Explorer'} />;
}

// ---------------- counting and the item squares ----------------

const itemCounts = new WeakMap<object, Map<string, { have: number; total: number }>>();

function useCountItems() {
  const { defs, hub, char } = useHub();
  return (node: number) => {
    let m = itemCounts.get(hub!);
    if (!m) { m = new Map(); itemCounts.set(hub!, m); }
    const key = `${node}|${char?.id ?? ''}`;
    const known = m.get(key);
    if (known) return known;
    const all = new Set<number>();
    const walk = (n: number) => { const d = defs!.nodes.get(n); if (!d) return; d.collectibles.forEach((c) => all.add(c)); d.nodes.forEach(walk); };
    walk(node);
    let have = 0, total = 0;
    for (const c of all) {
      if (!defs!.collectibles.has(c)) continue;
      const o = hub!.owned(c, char?.id);
      if (o < 0) continue;
      total++; have += o;
    }
    const r = { have, total };
    m.set(key, r);
    return r;
  };
}

const FAVS = 'd2c.pins.items';
const readFavs = (): number[] => { try { return JSON.parse(localStorage.getItem(FAVS) ?? '[]'); } catch { return []; } };
function toggleFav(c: number) {
  const f = readFavs();
  try { localStorage.setItem(FAVS, JSON.stringify(f.includes(c) ? f.filter((x) => x !== c) : [c, ...f])); } catch { /* storage blocked */ }
}

/** One collectible: bright when collected, dim when not; the tooltip says where it comes from; right-click to favourite. */
function CollectibleTile({ c, size = 64, onFav }: { c: number; size?: number; onFav?: () => void }) {
  const { defs, hub, char } = useHub();
  const nav = useNavigate();
  const def = defs!.collectibles.get(c);
  if (!def) return null;
  const owned = hub!.owned(c, char?.id);
  if (owned < 0) return null;
  const fav = readFavs().includes(c);
  return (
    <span className={`g-collect ${owned === 1 ? 'have' : ''}`} style={{ width: size, height: size, cursor: def.item ? 'pointer' : undefined }}
      title={`${def.name}\n${owned === 1 ? 'Collected' : 'Not collected yet'}${def.source ? '\n' + def.source : ''}${def.item ? '\n\nClick to open it in D2 Info' : ''}\nRight-click to ${fav ? 'take it out of' : 'add it to'} your favourites`}
      onClick={() => { if (def.item) nav(`/guardian/operations/d2-info?item=${def.item}`); }}
      onContextMenu={(e) => { e.preventDefault(); toggleFav(c); onFav?.(); }}>
      <Pic src={def.icon} lazy />
      {fav && <span className="g-collect-fav">★</span>}
    </span>
  );
}

// ---------------- Items: the front, then categories down to the items ----------------

function Items({ explorer }: { explorer: boolean }) {
  const h = useHub();
  const defs = h.defs!;
  const count = useCountItems();
  const [path, setPath] = useState<number[]>([]);
  const [badges, setBadges] = useState(false);
  const [missing, setMissing] = useState(false);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState('');
  const [, bump] = useState(0);
  const top = () => window.scrollTo({ top: 0 });

  if (!explorer && path.length === 0 && !badges) {
    const root = defs.roots.collections;
    const kids = (defs.nodes.get(root)?.nodes ?? []).filter((n) => defs.nodes.has(n) && count(n).total > 0);
    const all = count(root);
    let found: ReactNode[];
    let label: string, countText: string;
    if (search.length > 1) {
      const q = search.toLowerCase();
      const hits = [...defs.collectibles.entries()].filter(([c, d]) => (d.name.toLowerCase().includes(q) || d.source.toLowerCase().includes(q)) && h.hub!.owned(c, h.char?.id) >= 0).slice(0, 72);
      found = hits.map(([c]) => <CollectibleTile key={c} c={c} size={58} onFav={() => bump((x) => x + 1)} />);
      label = 'Search'; countText = hits.length === 72 ? 'first 72' : String(hits.length);
    } else {
      const recent = h.hub!.recent.filter((c) => defs.collectibles.has(c) && h.hub!.owned(c, h.char?.id) >= 0).slice(0, 36);
      found = recent.map((c) => <CollectibleTile key={c} c={c} size={58} onFav={() => bump((x) => x + 1)} />);
      label = 'Recently discovered'; countText = String(recent.length);
    }
    const favs = readFavs().filter((c) => defs.collectibles.has(c));
    const badgeNodes = (defs.nodes.get(defs.roots.badges)?.nodes ?? []).filter((n) => defs.nodes.has(n) && count(n).total > 0);
    const cols: number[][] = [[], [], []];
    badgeNodes.forEach((b, i) => cols[i % 3].push(b));
    return (
      <>
        <div className="g-chome">
          <Labelled label="Items" count={`${all.have.toLocaleString()} / ${all.total.toLocaleString()}`}>
            <div className="g-ctiles">
              {kids.map((k) => {
                const n = defs.nodes.get(k)!;
                const c = count(k);
                return (
                  <button key={k} type="button" className="g-ctile" onClick={() => { setPath([k]); top(); }}>
                    <Pic src={n.icon} className="g-ctile-icon" />
                    <span className="g-ctile-foot">
                      <span className="rule" />
                      <b>{n.name.toUpperCase()}</b>
                      <span className="rule" />
                      <span><b className="white">{c.have.toLocaleString()}</b><span className="g-dim"> / {c.total.toLocaleString()}</span></span>
                      <span className="rule" />
                    </span>
                  </button>
                );
              })}
            </div>
          </Labelled>
          <div className="g-stack big">
            <input className="g-input" placeholder="<enter name or description>" value={draft} onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && setSearch(draft.trim())} aria-label="Search collections" />
            <Labelled label={label} count={countText} style={{ minHeight: 200 }}>
              {found.length ? <div className="g-wrap g6">{found}</div> : <p className="g-text2">{search.length > 1 ? 'Nothing matches.' : 'Nothing discovered recently.'}</p>}
            </Labelled>
            <Labelled label="Favourites" count={String(favs.length)} style={{ minHeight: 120 }}>
              {favs.length ? <div className="g-wrap g6">{favs.map((c) => <CollectibleTile key={c} c={c} size={58} onFav={() => bump((x) => x + 1)} />)}</div>
                : <p className="g-text2">Right-click any collection item to keep it here.</p>}
            </Labelled>
          </div>
        </div>
        {badgeNodes.length > 0 && (
          <Labelled label="Badges" count={`${badgeNodes.filter((n) => { const c = count(n); return c.have === c.total; }).length} / ${badgeNodes.length}`} style={{ marginTop: 26 }}>
            <div className="g-cols three">
              {cols.map((col, i) => (
                <div key={i} className="g-stack tight">
                  {col.map((b) => {
                    const c = count(b);
                    return <FillRow key={b} icon={defs.nodes.get(b)!.icon} name={defs.nodes.get(b)!.name} done={c.have} total={c.total} tone={c.have === c.total ? 'gold' : 'plain'} onClick={() => { setBadges(true); setPath([b]); top(); }} />;
                  })}
                </div>
              ))}
            </div>
          </Labelled>
        )}
      </>
    );
  }

  const root = badges ? defs.roots.badges : defs.roots.collections;
  const here = path.length ? path[path.length - 1] : root;
  const node = defs.nodes.get(here);
  const kids = (node?.nodes ?? []).filter((n) => defs.nodes.has(n) && count(n).total > 0).filter((k) => { const c = count(k); return !(missing && c.have === c.total); });
  const shown = (node?.collectibles ?? []).filter((c) => !(missing && h.hub!.owned(c, h.char?.id) === 1));
  return (
    <>
      <div className="g-tools"><Check label="Only what I'm missing" on={missing} set={setMissing} /></div>
      <div className="g-crumbs">
        {[root, ...path].map((n, i) => (
          <span key={i}>
            {i > 0 && <span className="g-dim">/</span>}
            <button type="button" className={i === path.length ? 'on' : ''} onClick={() => { setPath(path.slice(0, i)); if (i === 0) setBadges(false); }}>
              {i === 0 ? 'Collections' : defs.nodes.get(n)?.name ?? '?'}
            </button>
          </span>
        ))}
      </div>
      {kids.length > 0 && (
        <div className="g-wrap">
          {kids.map((k) => {
            const c = count(k);
            const n = defs.nodes.get(k)!;
            return (
              <button key={k} type="button" className="g-card g-nodetile" onClick={() => { setPath([...path, k]); top(); }}>
                <Pic src={n.icon} width={44} height={44} />
                <span className="g-nodetile-words">
                  <b>{n.name}</b>
                  <Bar fraction={c.total ? c.have / c.total : 0} color={c.have === c.total ? 'var(--good)' : '#ffffff'} />
                  <span className={c.have === c.total ? 'good small' : 'g-text2 small'}>{c.have} / {c.total}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
      {(node?.collectibles.length ?? 0) > 0 && (
        shown.length ? <div className="g-wrap g8" style={{ marginTop: 6 }}>{shown.map((c) => <CollectibleTile key={c} c={c} onFav={() => bump((x) => x + 1)} />)}</div>
          : <p className="g-text2">{missing ? 'Nothing missing here.' : 'Nothing to show here.'}</p>
      )}
    </>
  );
}

// ---------------- a page over one of the game's category trees ----------------

/** Title and the tree's categories on the left, the chosen category's content on the right (its own, then each sub-category). */
function Tree({ id, title, blurb, root, fill, count, extra }: { id: string; title: string; blurb: string; root: number; fill: (n: TriumphNode) => ReactNode | null; count?: (n: number) => string; extra?: ReactNode }) {
  const h = useHub();
  const defs = h.defs!;
  const [pick, setPick] = useState<Record<string, number>>({});
  const start = inner(defs, root);
  const counter = count ?? ((n: number) => { const c = countUnder(defs, h.hub!, h.char?.id, n); return c.total ? `${c.done} / ${c.total}` : ''; });
  const kids = (defs.nodes.get(start)?.nodes ?? []).filter((n) => defs.nodes.has(n));
  const chosen = kids.length === 0 ? start : kids.includes(pick[id]) ? pick[id] : kids[0];
  const sections: ReactNode[] = [];
  const section = (hash: number, depth: number) => {
    const node = defs.nodes.get(hash);
    if (!node) return;
    const content = fill(node);
    if (content) {
      if (depth > 0) sections.push(<Heading key={`h${hash}`} style={{ marginTop: sections.length ? 22 : 0 }}>{node.name.toUpperCase().split('').join(' ')}</Heading>);
      sections.push(<div key={`c${hash}`}>{content}</div>);
    }
    node.nodes.forEach((c) => section(c, depth + 1));
  };
  section(chosen, 0);
  return (
    <Split
      left={(
        <>
          <h1 className="g-title light">{title}</h1>
          <p className="g-text2 big14" style={{ margin: '6px 0 12px' }}>{blurb}</p>
          {extra}
          {kids.map((k) => <ListRow key={k} name={defs.nodes.get(k)!.name} count={counter(k)} selected={k === chosen} onClick={() => setPick({ ...pick, [id]: k })} />)}
        </>
      )}
      right={sections.length ? sections : <p className="g-text2">Nothing to show here.</p>}
    />
  );
}

function RecordCards({ node }: { node: TriumphNode }) {
  const h = useHub();
  const shown = node.records.filter((r) => !h.hub!.record(r, h.char?.id)?.hidden && h.defs!.records.has(r));
  if (!shown.length) return null;
  return <div className="g-wrap g-records570">{shown.map((r) => <RecordRow key={r} hash={r} />)}</div>;
}

function MedalCards({ node }: { node: TriumphNode }) {
  const h = useHub();
  const live = (r: number) => h.hub!.record(r, h.char?.id);
  const shown = node.records.filter((r) => !live(r)?.hidden && h.defs!.records.has(r));
  if (!shown.length) return null;
  const earned = (r: number) => live(r)?.objectives[0]?.progress ?? 0;
  return (
    <div className="g-wrap g8">
      {[...shown].sort((a, b) => earned(b) - earned(a)).map((r) => {
        const def = h.defs!.records.get(r)!;
        const n = earned(r);
        return (
          <div key={r} className={`g-medalcard ${n > 0 ? 'on' : ''}`} title={`${def.name}\n${def.desc}\n\nEarned ${n.toLocaleString()} times`}>
            <Pic src={def.icon} width={52} height={52} style={{ opacity: n > 0 ? 1 : 0.35 }} />
            <span><b>{def.name}</b><small className="clamp2">{def.desc}</small></span>
            <span className={n > 0 ? 'gold' : 'g-dim'}>{n.toLocaleString()}</span>
          </div>
        );
      })}
    </div>
  );
}

export const clock = (seconds: number) => {
  if (seconds <= 0) return '-';
  const hh = Math.floor(seconds / 3600), mm = Math.floor((seconds % 3600) / 60), ss = Math.floor(seconds % 60);
  return hh >= 1 ? `${hh}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}` : `${mm}:${String(ss).padStart(2, '0')}`;
};

function MetricTiles({ node }: { node: TriumphNode }) {
  const h = useHub();
  // Bungie's data lists some trackers twice under one heading: each is shown once
  const shown = [...new Set(node.metrics)].filter((m) => h.defs!.metrics.has(m));
  if (!shown.length) return null;
  return (
    <div className="g-wrap g8">
      {shown.map((m) => {
        const def = h.defs!.metrics.get(m)!;
        const has = m in h.hub!.metrics;
        const value = h.hub!.metrics[m] ?? 0;
        // the game keeps times in milliseconds
        const isTime = /time|fastest/i.test(def.name) || /fastest/i.test(def.desc);
        const text = !has ? '-' : isTime ? clock(value / 1000) : value.toLocaleString();
        return (
          <div key={m} className={`g-metric ${has && value > 0 ? 'on' : ''}`} title={`${def.name}\n${def.desc}`}>
            <b>{text}</b>
            <small className="clamp2">{def.name}</small>
            <Pic src={def.icon} className="g-metric-icon" />
          </div>
        );
      })}
    </div>
  );
}

// ---------------- Lore ----------------

function Lore() {
  const h = useHub();
  const defs = h.defs!;
  const [book, setBook] = useState(0);
  const [entry, setEntry] = useState(0);
  const [lore, setLore] = useState<Record<string, any>>();
  useEffect(() => { if (book) table<any>('DestinyLoreDefinition').then(setLore).catch(() => setLore({})); }, [book]);
  const live = (r: number) => h.hub!.record(r, h.char?.id);
  if (book && defs.nodes.has(book)) {
    const b = defs.nodes.get(book)!;
    const pages = b.records.filter((r) => defs.records.get(r)?.lore);
    const c = countUnder(defs, h.hub!, h.char?.id, book);
    const open = pages.includes(entry) ? entry : pages[0];
    const page = open ? lore?.[defs.records.get(open)!.lore]?.displayProperties : undefined;
    return (
      <Split
        left={(
          <>
            <div style={{ marginBottom: 10 }}><SmallButton onClick={() => { setBook(0); setEntry(0); }}>Back to the books</SmallButton></div>
            <h2 className="g-booktitle">{b.name}</h2>
            <p className="g-dim small" style={{ marginBottom: 10 }}>{c.done} / {c.total} pages found</p>
            {pages.map((r) => <ListRow key={r} name={defs.records.get(r)!.name} count={live(r)?.done ? '' : 'not found'} selected={r === open} onClick={() => setEntry(r)} />)}
          </>
        )}
        right={!pages.length ? <p className="g-text2">This book has no pages to read.</p> : (
          <div className="g-lorepage">
            <h2>{page?.name || defs.records.get(open)!.name}</h2>
            <p>{lore ? page?.description || 'The text of this page is not in the database.' : 'Reading the lore from Bungie\'s database...'}</p>
          </div>
        )}
      />
    );
  }
  return (
    <Tree id="Lore" title="Lore" blurb="The books of the Destiny universe, page by page. Open a book to read it; pages you have not found in the game yet are marked."
      root={defs.more.lore} count={(n) => { const k = defs.nodes.get(n)?.nodes.length ?? 0; return k ? `${k} books` : ''; }}
      fill={(node) => {
        const books = node.nodes.filter((n) => (defs.nodes.get(n)?.records.length ?? 0) > 0);
        if (!books.length) return null;
        return (
          <div className="g-wrap g-books">
            {books.map((bk) => {
              const b = defs.nodes.get(bk)!;
              const c = countUnder(defs, h.hub!, h.char?.id, bk);
              return (
                <button key={bk} type="button" className="g-book" onClick={() => { setBook(bk); setEntry(0); window.scrollTo({ top: 0 }); }}>
                  <span className="g-book-cover"><Pic src={b.icon} style={{ opacity: c.done > 0 ? 1 : 0.4 }} lazy /></span>
                  <b className="clamp2">{b.name}</b>
                  <span className={c.done === c.total ? 'good small' : 'g-dim small'}>{c.done} / {c.total}</span>
                </button>
              );
            })}
          </div>
        );
      }} />
  );
}

// ---------------- Patterns & Catalysts ----------------

function Patterns() {
  const h = useHub();
  const defs = h.defs!;
  const [topIdx, setTop] = useState(0);
  const tops = (defs.nodes.get(inner(defs, defs.more.catalysts))?.nodes ?? []).filter((n) => defs.nodes.has(n)).map((n) => ({ name: defs.nodes.get(n)!.name, node: n }));
  if (!tops.length) return <Message title="Patterns & Catalysts" text="The game's list of patterns and catalysts could not be read." />;
  const t = Math.min(topIdx, tops.length - 1);
  return (
    <Tree key={t} id={`PC${t}`} title="Patterns & Catalysts" blurb="Weapon patterns you are collecting for the Enclave, and the catalysts for your exotic weapons, each with how far along it is."
      root={tops[t].node} fill={(n) => <RecordCards node={n} />}
      extra={(
        <select className="g-input g-select" style={{ fontSize: 16, marginBottom: 10 }} value={t} onChange={(e) => setTop(Number(e.target.value))} aria-label="Patterns or catalysts">
          {tops.map((x, i) => <option key={x.node} value={i}>{x.name}</option>)}
        </select>
      )} />
  );
}

// ---------------- Sets: everything one raid or dungeon can drop ----------------

const SET_SOURCE = /"([^"]+)"\s+(Raid|Dungeon)/i;

function Sets() {
  const h = useHub();
  const defs = h.defs!;
  const [pick, setPick] = useState('');
  const owned = (c: number) => h.hub!.owned(c, h.char?.id);
  const sets = new Map<string, { kind: string; items: number[] }>();
  for (const [hash, c] of defs.collectibles) {
    const m = SET_SOURCE.exec(c.source);
    if (!m || owned(hash) < 0) continue;
    const s = sets.get(m[1]) ?? { kind: m[2][0].toUpperCase() + m[2].slice(1).toLowerCase(), items: [] };
    s.items.push(hash);
    sets.set(m[1], s);
  }
  if (!sets.size) return <Message title="Sets" text="No raid or dungeon items found in Collections." />;
  const names = [...sets.keys()].sort((a, b) => (sets.get(a)!.kind === 'Raid' ? 0 : 1) - (sets.get(b)!.kind === 'Raid' ? 0 : 1) || a.localeCompare(b));
  const chosenName = sets.has(pick) ? pick : names[0];
  const chosen = sets.get(chosenName)!;
  const have = chosen.items.filter((i) => owned(i) === 1).length;
  let kind = '';
  return (
    <Split
      left={(
        <>
          <h1 className="g-title">Sets</h1>
          <p className="g-text2 big14" style={{ margin: '6px 0 12px' }}>Everything a raid or dungeon can drop that Collections keeps track of, and how much of it you have.</p>
          {names.map((n) => {
            const s = sets.get(n)!;
            const head = s.kind !== kind ? <Heading key={`h${s.kind}`} style={{ margin: '12px 0 4px' }}>{(s.kind + 's').toUpperCase().split('').join(' ')}</Heading> : null;
            kind = s.kind;
            return <div key={n}>{head}<ListRow name={n} count={`${s.items.filter((i) => owned(i) === 1).length} / ${s.items.length}`} selected={n === chosenName} onClick={() => setPick(n)} /></div>;
          })}
        </>
      )}
      right={(
        <>
          <div className="g-sethead">Sets // {chosenName}</div>
          <p className="g-text2 big14">{chosen.kind}  |  {have} of {chosen.items.length} collected  |  {Math.round((100 * have) / chosen.items.length)} %</p>
          <div style={{ maxWidth: 700, margin: '2px 0 12px' }}><Bar fraction={have / chosen.items.length} color={have === chosen.items.length ? 'var(--good)' : '#ffffff'} height={5} /></div>
          <div className="g-wrap g8">{[...chosen.items].sort((a, b) => defs.collectibles.get(a)!.name.localeCompare(defs.collectibles.get(b)!.name)).map((i) => <CollectibleTile key={i} c={i} size={72} />)}</div>
        </>
      )}
    />
  );
}

// ---------------- Armor Set Bonuses ----------------

interface ArmorSetPiece { name: string; icon: string; kind: string; cls: number; collectible: number }
interface ArmorSet { name: string; source: string; perks: { pieces: number; name: string; desc: string; icon: string }[]; items: ArmorSetPiece[] }

let armorSets: Promise<ArmorSet[]> | null = null;
function loadArmorSets(): Promise<ArmorSet[]> {
  armorSets ??= (async () => {
    const [t, lite, collectibles] = await Promise.all([tablesOf('DestinyEquipableItemSetDefinition', 'DestinySandboxPerkDefinition'), items(), table<any>('DestinyCollectibleDefinition')]);
    const list: ArmorSet[] = [];
    for (const s of Object.values<any>(t.DestinyEquipableItemSetDefinition)) {
      const name = s.displayProperties?.name ?? '';
      const pieces: ArmorSetPiece[] = (s.setItems ?? []).map((h: number) => lite[h]).filter((d: any) => d?.name)
        .map((d: any) => ({ name: d.name, icon: d.icon, kind: d.type, cls: d.classType, collectible: d.collectible }));
      if (!name || !pieces.length) continue;
      const perks = (s.setPerks ?? []).map((p: any) => ({ p, d: t.DestinySandboxPerkDefinition[p.sandboxPerkHash]?.displayProperties }))
        .filter((x: any) => x.d).map((x: any) => ({ pieces: x.p.requiredSetCount ?? 0, name: x.d.name, desc: x.d.description, icon: x.d.icon }))
        .sort((a: any, b: any) => a.pieces - b.pieces);
      const source = pieces.map((i) => collectibles[i.collectible]?.sourceString ?? '').find(Boolean) ?? '';
      list.push({ name, source, perks, items: pieces.sort((a, b) => a.cls - b.cls) });
    }
    return list;
  })().catch((e) => { armorSets = null; throw e; });
  return armorSets;
}

function ArmorSetBonuses() {
  const h = useHub();
  const nav = useNavigate();
  const [sets, setSets] = useState<ArmorSet[]>();
  useEffect(() => { loadArmorSets().then(setSets).catch(() => setSets([])); }, []);
  if (!sets) return <Message title="Armor Set Bonuses" text="Reading the armour sets from Bungie's database..." />;
  const has = (i: ArmorSetPiece) => i.collectible !== 0 && h.hub!.owned(i.collectible, h.char?.id) === 1;
  const classes = ['Titan', 'Hunter', 'Warlock'];
  return (
    <Split
      left={(
        <>
          <h1 className="g-title">Armor Set Bonuses</h1>
          <p className="g-text2 big14" style={{ marginTop: 6 }}>Some armor sets provide special bonuses when multiple pieces from the same set are equipped. Each set below shows its two bonuses and its pieces; the bright pieces are the ones you have collected.</p>
        </>
      )}
      right={!sets.length ? <p className="g-text2">No armour sets found in the database.</p> : sets.map((set, k) => {
        const owned = set.items.filter(has).length;
        return (
          <div key={set.name + k} className="g-armorset" style={{ paddingTop: k === 0 ? 0 : 26 }}>
            <div className="g-armorset-title"><span>{set.name}</span><span className={owned === set.items.length ? 'good small' : 'g-dim small'}>{owned} / {set.items.length}</span></div>
            <p className="g-armorset-src">{set.source || 'Unknown'}</p>
            <div className="g-armorset-body">
              <div className="g-stack" style={{ gap: 14 }}>
                {set.perks.map((p) => (
                  <div key={p.name} className="g-armorperk">
                    <span className="g-armorperk-icon"><Pic src={p.icon} /></span>
                    <div><b>{p.name}</b> <span className="gold small">{p.pieces} pieces</span><p className="g-text2">{p.desc}</p></div>
                  </div>
                ))}
              </div>
              <div className="g-cols three tight">
                {/* the game's class numbers are Titan 0, Hunter 1, Warlock 2; shown Hunter, Titan, Warlock */}
                {[1, 0, 2].map((cls) => (
                  <div key={cls} className="g-stack" style={{ gap: 4 }}>
                    {set.items.filter((i) => i.cls === cls).map((i) => (
                      <button type="button" key={i.name} className={`g-piece ${has(i) ? 'have' : ''}`} title={`${i.name}\n${classes[cls]} ${i.kind}\n${has(i) ? 'Collected' : 'Not collected yet'}\n\nClick to open it in D2 Info`}
                        onClick={() => { const item = h.defs?.collectibles.get(i.collectible)?.item; if (item) nav(`/guardian/operations/d2-info?item=${item}`); }}>
                        <Pic src={i.icon} width={36} height={36} lazy />
                        <span><span className="n">{i.name}</span><small>{i.kind}</small></span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    />
  );
}
